import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, of, switchMap } from 'rxjs';
import { HomeActions } from '@states/home/home.action-types';
import { HomeService } from '../../development/home.service';
import { SharedActions } from '@states/shared/shared.action-types';
import { HomeModel } from '@models/home.model';
import { withLatestFrom } from 'rxjs/operators';
import { Action, select, Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { UtilsService } from '../../edge/utils.service';
import { TranslateService } from '@ngx-translate/core';
import { HomeSelectors } from '@states/home/home.selector-types';
import { SearchableActions } from '@states/searchable/searchable.action-types';
import { DatabaseTagsActions } from '@states/database-tags/database-tags.action.types';
import { LocationActions } from '@states/location/location.action-types';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';

@Injectable()
export class HomeEffects {

  public getFavoriteLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getFavoriteLocations),
      switchMap(() => {
        return this.homeService.getFavorites()
          .pipe(
            switchMap((res) => {
              const favorites: string[] = [
                ...(res.locations ?? []),
                ...(res.walls ?? []),
                ...(res.cameras ?? []),
              ];
              return [HomeActions.getFavoriteLocationsSuccess({ favorites })];
            }),
            catchError((err) => {
                const msg = this.utilsService.errMessage(err);
                return [SharedActions.showMessage({ error: this.translateService.instant('TOASTS.FAILED_STAR_LOCATION', { err: msg }) })];
              },
            ));
      }),
    ),
  );

  public starLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.starLocation),
      switchMap(({ id }) => {
        return this.homeService.starLocation(id)
          .pipe(
            switchMap(() => {
              return [];
            }),
            catchError((err) => {
                const msg = this.utilsService.errMessage(err);
                return [SharedActions.showMessage({ error: this.translateService.instant('TOASTS.FAILED_STAR_LOCATION', { err: msg }) })];
              },
            ));
      }),
    ),
  );

  public unstarLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.unstarLocation),
      switchMap(({ id }) => {
        return this.homeService.unstarLocation(id)
          .pipe(
            switchMap(() => {
              return [];
            }),
            catchError((err) => {
                const msg = this.utilsService.errMessage(err);
                return [SharedActions.showMessage({ error: this.translateService.instant('TOASTS.FAILED_UN_STAR_LOCATION', { err: msg }) })];
              },
            ));
      }),
    ),
  );

  public starCamera$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.starCamera),
      switchMap(({ id }) => {
        return this.homeService.starCamera(id)
          .pipe(
            switchMap(() => {
              return [];
            }),
            catchError((err) => {
                const msg = this.utilsService.errMessage(err);
                return [SharedActions.showMessage({ error: this.translateService.instant('TOASTS.FAILED_ADD_FAVORITE', { err: msg }) })];
              },
            ));
      }),
    ),
  );

  public unstarCamera$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.unstarCamera),
      switchMap(({ id }) => {
        return this.homeService.unstarCamera(id)
          .pipe(
            switchMap(() => {
              return [];
            }),
            catchError((err) => {
                const msg = this.utilsService.errMessage(err);
                return [SharedActions.showMessage({ error: this.translateService.instant('TOASTS.FAILED_REMOVE_FAVORITE', { err: msg }) })];
              },
            ));
      }),
    ),
  );

  public starWall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.starWall),
      switchMap(({ id }) => {
        return this.homeService.starWall(id)
          .pipe(
            switchMap(() => {
              return [];
            }),
            catchError((err) => {
                const msg = this.utilsService.errMessage(err);
                return [SharedActions.showMessage({ error: this.translateService.instant('TOASTS.FAILED_ADD_FAVORITE', { err: msg }) })];
              },
            ));
      }),
    ),
  );

  public unstarWall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.unstarWall),
      switchMap(({ id }) => {
        return this.homeService.unstarWall(id)
          .pipe(
            switchMap(() => {
              return [];
            }),
            catchError((err) => {
                const msg = this.utilsService.errMessage(err);
                return [SharedActions.showMessage({ error: this.translateService.instant('TOASTS.FAILED_REMOVE_FAVORITE', { err: msg }) })];
              },
            ));
      }),
    ),
  );

  public getLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getLocations),
      withLatestFrom(
        this.store$.pipe(select(state => state.locationState)),
        this.store$.pipe(select(state => state.cameraState)),
        this.store$.pipe(select(OrganizationSelectors.selectEnableGlobalSearch)),
      ),
      switchMap(([, { entities }, locationCameras, enableGlobalSearch]) => {
        return this.homeService.getHomeLocations()
          .pipe(
            switchMap((locations) => {
              /**
               * need to normalize HOME cameras to be same as location cameras to avoid race.
               */
              const cameraEntities = locationCameras.entities;
              const normalizedLocationWithCameras = locations.map(location => {
                return {
                  ...location,
                  cameras: (location.cameras ?? []).filter(camera => cameraEntities[camera.cameraId]),
                };
              });
              const actions: Action[] = [
                HomeActions.getLocationsSuccess({ locations: normalizedLocationWithCameras }),
              ];
              if (enableGlobalSearch) {
                actions.push(LocationActions.GetLocationsForGlobalSearch());
              }
              return actions;
            }),
            catchError((err) => {
              return [SharedActions.consoleMessage({ error: `Failed to get home locations: ${JSON.stringify(err?.message)}` })];
            }),
          );
      }),
    ),
  );

  public serverRequestCreateSubLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.serverRequestCreateSubLocation),
      switchMap(({ sublocation }) => {
        return this.homeService.addSubLocation(sublocation)
          .pipe(
            switchMap((document) => {
              return [HomeActions.serverRequestCreateSubLocationSuccess({ document })];
            }),
            catchError((err) => {
              const msg = this.utilsService.errMessage(err);
              return [SharedActions.showMessage({ error: this.translateService.instant('TOASTS.FAILED_TO_CREATE_LOCATION', { err: msg }) })];
            }),
          );
      }),
    ),
  );

  public addCamerasToSubLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.addCamerasToSubLocation),
      withLatestFrom(this.store$.pipe(select(state => state.homeState))),
      switchMap(([{ sublocationId }, { selectedCameras }]) => {
        if (selectedCameras.length === 0) {
          return [SharedActions.showMessage({ warning: this.translateService.instant('TOASTS.NO_CAMERAS_SELECTED') })];
        }
        const request: HomeModel.AddCamerasToSubLocationRequest = {
          subLocationId: sublocationId,
          cameras: selectedCameras,
        };
        return this.homeService.addCamerasToSubLocation(request)
          .pipe(
            switchMap((res) => {
              return [HomeActions.getLocations(), HomeActions.clearCameraSelectionMode()];
            }),
            catchError((err) => {
                const msg = this.utilsService.errMessage(err);
                return [SharedActions.showMessage({ error: this.translateService.instant('TOASTS.FAILED_TO_ADD_CAMERA', { err: msg }) })];
              },
            ));
      }),
    ),
  );

  public addCameraToSubLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.addCameraToSubLocation),
      withLatestFrom(
        this.store$.pipe(select(state => state.homeState)),
        this.store$.pipe(select(state => state.cameraState)),
      ),
      switchMap(([{ request }, homeState, cameraState]) => {
        const sublocation = homeState.entities[request.subLocationId];
        const sublocationName = sublocation?.name || this.translateService.instant('COMMON.LOCATION');
        const camera = cameraState.entities[request.camera.cameraId];
        const cameraName = camera?.edgeOnly?.name || this.translateService.instant('COMMON.CAMERA');
        return this.homeService.addCameraToSubLocation(request)
          .pipe(
            switchMap((res) => {
              return [SharedActions.showMessage({ success: this.translateService.instant('TOASTS.DEVICE_MOVED_TO', { name: cameraName, target: sublocationName }) }), HomeActions.getLocations()];
            }),
            catchError((err) => {
                const msg = this.utilsService.errMessage(err);
                return [SharedActions.showMessage({ error: this.translateService.instant('TOASTS.FAILED_TO_ADD_CAMERA', { err: msg }) })];
              },
            ));
      }),
    ),
  );

  public addDeviceToSubLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.addDeviceToSubLocation),
      withLatestFrom(this.store$.pipe(select(state => state.homeState))),
      switchMap(([{ request, deviceName }, homeState]) => {
        const target = homeState.entities[request.subLocationId];
        const targetName = target?.name || this.translateService.instant('COMMON.LOCATION');
        return this.homeService.addDeviceToSubLocation(request)
          .pipe(
            switchMap(() => {
              return [SharedActions.showMessage({ success: this.translateService.instant('TOASTS.DEVICE_MOVED_TO', { name: deviceName, target: targetName }) }), HomeActions.getLocations()];
            }),
            catchError((err) => {
                const msg = this.utilsService.errMessage(err);
                return [SharedActions.showMessage({ error: this.translateService.instant('TOASTS.FAILED_TO_MOVE_DEVICE', { err: msg }) })];
              },
            ));
      }),
    ),
  );

  public removeCameraFromSubLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.removeCameraFromSubLocation),
      switchMap(({ request }) => {
        return this.homeService.removeCameraFromSubLocation(request)
          .pipe(
            switchMap((res) => {
              return [HomeActions.getLocations()];
            }),
            catchError((err) => {
                const msg = this.utilsService.errMessage(err);
                return [SharedActions.showMessage({ error: this.translateService.instant('TOASTS.FAILED_TO_REMOVE_CAMERA', { err: msg }) })];
              },
            ));
      }),
    ),
  );

  public deleteSubLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.deleteSubLocation),
      switchMap(({ request }) => {
        return this.homeService.deleteSubLocation(request)
          .pipe(
            switchMap((res) => {
              return [HomeActions.getLocations()];
            }),
            catchError((err) => {
                const msg = this.utilsService.errMessage(err);
                return [SharedActions.showMessage({ error: this.translateService.instant('TOASTS.FAILED_TO_DELETE_SUB_LOCATION', { err: msg }) })];
              },
            ));
      }),
    ),
  );

  public updateSubLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.updateSubLocation),
      switchMap(({ request }) => {
        return this.homeService.updateSubLocation(request)
          .pipe(
            switchMap((res) => {
              return [HomeActions.getLocations()];
            }),
            catchError((err) => {
                const msg = this.utilsService.errMessage(err);
                return [SharedActions.showMessage({ error: this.translateService.instant('TOASTS.FAILED_TO_UPDATE_SUB_LOCATION', { err: msg }) })];
              },
            ));
      }),
    ),
  );

  public dataReady$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.setQuery, HomeActions.getLocationsSuccess),
      withLatestFrom(
        this.store$.select(HomeSelectors.selectQueryFilter),
        this.store$.select(HomeSelectors.selectInitialLoaded),
      ),
      filter(([, query, initialLoaded]) => query !== null && initialLoaded),
      map(() => HomeActions.locationFilteringComplete()),
    ),
  );

  public onTagRemoved$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatabaseTagsActions.onTagRemoved),
      switchMap(({ id }) => {
        return [
          HomeActions.removeTagFromHomeLocations({ id: id }),
        ];
      }),
    ),
  );

  constructor(private actions$: Actions,
              private homeService: HomeService,
              private store$: Store<AppState>,
              private utilsService: UtilsService,
              private translateService: TranslateService) {
  }
}
